import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { AssetDto, AssetVersionDto } from '@nusign/picflow-backend-types';
import { firstValueFrom, lastValueFrom } from 'rxjs';
import { downloadAsset } from '../gallery/lib/download-asset';
import { addGalleryPassword } from '../lib/add-gallery-password';
import { estimateResizedAssetSize } from '../lib/estimate-resized-asset-size';
import { sleep } from '../lib/sleep';
import { IGNORE_ALL_ERRORS, IGNORE_AUTH_ERRORS } from '../network-interceptor';
import { PICFLOW_CONFIG } from '../picflow-config';
import { GalleryPasswordService } from './gallery-password.service';
import { SentryService } from './sentry.service';
import { StateService } from './state.service';
import { StorageService } from './storage.service';
import { ToastService } from './toast.service';
import { VirusScanService } from './virus-scan.service';

@Injectable({
  providedIn: 'root',
})
export class DownloadAssetService {
  private toastService = inject(ToastService);
  private config = inject(PICFLOW_CONFIG);
  private storage = inject(StorageService);
  private http = inject(HttpClient);
  private galleryPasswordService = inject(GalleryPasswordService);
  private virusScanService = inject(VirusScanService);
  private sentryService = inject(SentryService);
  private state = inject(StateService);

  public async downloadAssetVersions(
    assets: AssetDto[],
    versions: AssetVersionDto[],
    galleryId: string,
    longEdge?: number,
    reportDownloads = true
  ) {
    if (this.state.galleryPreviewMode) {
      return this.toastService.add('Downloads are not available in preview mode', 'error');
    }

    const toast = this.toastService.add('Downloading...', 'info', {
      duration: undefined,
      icon: '/assets/icons/download.svg',
    });

    try {
      await Promise.all(assets.map((_, i) => firstValueFrom(this.virusScanService.onScanned(versions[i]))));
    } catch (err) {
      console.warn(err);
      this.toastService.close(toast);
      this.toastService.add('Download Failed, virus detected', 'error');
      return;
    }

    try {
      await Promise.all([
        ...assets.map((asset, i) =>
          downloadAsset(asset, versions[i], longEdge, this.config.assetsEndpoint, asset.watermark_enabled)
        ),
        sleep(1000),
      ]);
    } catch (err) {
      console.warn(err);
      this.sentryService.captureException('asset-download-failed');
      this.toastService.add('Download Failed. Please try again', 'error');
    }
    if (reportDownloads) {
      assets.forEach((asset, i) => this.reportSingleAssetDownload(asset.id, galleryId, versions[i].id));
    } else {
      assets.forEach((asset, i) =>
        this.reportSingleAssetDownloadInternal(
          asset.id,
          galleryId,
          asset.tenant,
          longEdge,
          estimateResizedAssetSize(versions[i], longEdge)
        )
      );
    }
    this.toastService.close(toast);
  }

  public async downloadAssetVersion(
    asset: AssetDto,
    version: AssetVersionDto,
    galleryId: string,
    longEdge?: number,
    reportDownloads = true
  ) {
    return this.downloadAssetVersions([asset], [version], galleryId, longEdge, reportDownloads);
  }

  private async reportSingleAssetDownload(assetId: string, galleryId: string, versionId: string) {
    const assetsDownloaded = this.storage.get<string[]>('downloaded_assets') || [];
    const assetDownloaded = assetsDownloaded.includes(assetId);
    if (assetDownloaded) return; // Only log one download per session
    this.storage.set('downloaded_assets', [...assetsDownloaded, assetId], true);
    try {
      await lastValueFrom(
        this.http.post(
          addGalleryPassword(
            `${this.config.apiEndpoint}/assets/${assetId}/downloads`,
            this.galleryPasswordService.getPassword(galleryId)
          ),
          { asset_version: versionId },
          {
            context: new HttpContext().set(IGNORE_AUTH_ERRORS, true),
          }
        )
      );
    } catch {
      console.error('Ignoring assetId download reporting error');
    }
  }

  private async reportSingleAssetDownloadInternal(
    assetId: string,
    galleryId: string,
    tenantId: string,
    longEdge: number,
    size: bigint
  ) {
    const assetsDownloaded = this.storage.get<string[]>('downloaded_assets') || [];
    const assetDownloaded = assetsDownloaded.includes(assetId);
    if (assetDownloaded) return; // Only log one download per session
    this.storage.set('downloaded_assets', [...assetsDownloaded, assetId], true);
    try {
      await lastValueFrom(
        this.http.post(
          `${this.config.correctPicflowUrl.replace('/embed', '')}/internal-api/report-embed-download`,
          {
            asset_id: assetId,
            gallery_id: galleryId,
            long_edge: longEdge,
            size: Number(size),
            url: window.location.href,
            tenant_id: tenantId,
          },
          {
            context: new HttpContext().set(IGNORE_ALL_ERRORS, true),
            responseType: 'text',
            withCredentials: false,
          }
        )
      );
    } catch {
      console.error('Ignoring assetId download reporting error');
    }
  }
}
